import {
  GENERATED_INITIAL_LESSON_LEXICON,
  GENERATED_INITIAL_READING_ID,
  GENERATED_LESSON_LEXICON_LOADERS,
  GENERATED_PUBLISHED_READINGS,
} from "./generated/reading-index";
import type { IntentDefinition, LessonLexicon, Reading, ReadingAppetite, ReadingAppetiteDefinition, ReadingArticulation, ReadingArticulationDefinition, ReadingCommitment, ReadingCommitmentDefinition, ReadingDifficultyDefinition, ReadingForm, ReadingIntent, ReadingLexicalDifficulty } from "./types";
import { assertValidCatalog } from "./validation";
import { computeReadingMetrics } from "./metrics";

export { computeReadingMetrics } from "./metrics";

export const READING_INTENTS: IntentDefinition[] = [
  {
    id: "complex-thought",
    label: "Express complex thoughts clearly",
    description: "Distil a layered idea without flattening it.",
    outcomes: ["precise thought", "conceptual distillation"],
    rhetoricalMoves: ["contrast", "compression", "reframing"],
  },
  {
    id: "tactful-confidence",
    label: "Speak with tactful confidence",
    description: "Carry a boundary or disagreement without aggression.",
    outcomes: ["tactful firmness", "composed authority"],
    rhetoricalMoves: ["precise assertion", "refusal", "concession"],
  },
  {
    id: "difficult-vocabulary",
    label: "Build difficult vocabulary",
    description: "Meet advanced words that remain useful beyond the page.",
    outcomes: ["lexical command", "precise thought"],
    rhetoricalMoves: ["contrast", "qualification"],
  },
  {
    id: "implication",
    label: "Understand implication",
    description: "Notice what a sentence makes present without stating it.",
    outcomes: ["emotional precision", "inference"],
    rhetoricalMoves: ["implication", "understatement", "strategic silence"],
  },
  {
    id: "rhythm",
    label: "Develop rhythm",
    description: "Feel how cadence changes authority and memory.",
    outcomes: ["cadence", "memorable expression"],
    rhetoricalMoves: ["parallelism", "compression", "controlled release"],
  },
  {
    id: "force",
    label: "Use language with more force",
    description: "Make the sentence carry weight without merely raising its volume.",
    outcomes: ["composed authority", "precise thought"],
    rhetoricalMoves: ["precise assertion", "contrast", "reframing"],
  },
];

export const READING_COMMITMENTS: ReadingCommitmentDefinition[] = [
  {
    id: "brief",
    label: "A few minutes",
    description: "One complete thought or scene.",
    targetMinutes: 5,
  },
  {
    id: "sitting",
    label: "A quiet sitting",
    description: "Enough room for a fuller turn.",
    targetMinutes: 15,
  },
  {
    id: "immersion",
    label: "Let me settle in",
    description: "A chaptered work with an exact return.",
    targetMinutes: 35,
  },
];

export const READING_APPETITES: ReadingAppetiteDefinition[] = [
  { id: "tense", label: "Something tense", moods: ["tense", "mysterious", "unsettling"] },
  { id: "warm", label: "Something warm", moods: ["warm", "tender", "elegiac"] },
  { id: "wondrous", label: "Something wondrous", moods: ["wondrous", "exuberant"] },
  { id: "wry", label: "Something sharp and funny", moods: ["wry", "bracing"] },
  { id: "thoughtful", label: "Something to think with", moods: ["lucid", "bracing", "elegiac"], craftFamilies: ["argument-essay", "speculative-discipline"] },
  { id: "surprise", label: "Surprise me", moods: [] },
];

export const READING_DIFFICULTIES: ReadingDifficultyDefinition[] = [
  { id: "approachable", label: "Comfortable", description: "Rich language with a gentler lexical load." },
  { id: "advanced", label: "Stretch me", description: "A deliberate step beyond everyday vocabulary." },
  { id: "advanced-plus", label: "Demanding", description: "Dense, exact language that asks for attention." },
];

export const READING_ARTICULATIONS: ReadingArticulationDefinition[] = [
  {
    id: "direct",
    label: "Direct",
    outcomes: ["precise thought", "composed authority"],
    rhetoricalMoves: ["precise assertion", "contrast"],
  },
  {
    id: "tactful",
    label: "Tactful",
    outcomes: ["tactful firmness", "composed authority"],
    rhetoricalMoves: ["concession", "qualification", "refusal"],
  },
  {
    id: "warm-authority",
    label: "Warm authority",
    outcomes: ["emotional precision", "composed authority"],
    rhetoricalMoves: ["understatement", "precise assertion"],
    moods: ["warm", "tender"],
  },
  {
    id: "restrained",
    label: "Restrained",
    outcomes: ["inference", "emotional precision"],
    rhetoricalMoves: ["implication", "understatement", "strategic silence"],
  },
  {
    id: "punchy",
    label: "Punchy",
    outcomes: ["memorable expression", "cadence", "wit"],
    rhetoricalMoves: ["compression", "parallelism", "reframing"],
  },
];

export const READING_FORM_LABELS: Record<ReadingForm, string> = {
  story: "Story",
  "dramatic-scene": "Dramatic scene",
  dialogue: "Dialogue",
  essay: "Essay",
  letter: "Letters",
  speech: "Speech",
  "fable-parable": "Fable or parable",
  document: "Document",
};

export const READINGS: Reading[] = assertValidCatalog([...GENERATED_PUBLISHED_READINGS]);

export const READING_BY_ID = new Map(READINGS.map((reading) => [reading.id, reading]));
export const READING_METRICS = new Map(READINGS.map((reading) => [reading.id, computeReadingMetrics(reading)]));
export const INITIAL_LESSON_LEXICON = GENERATED_INITIAL_LESSON_LEXICON;
export const AVAILABLE_READING_FORMS = [...new Set(READINGS.map((reading) => reading.taxonomy.form))];

if (READINGS[0]?.id !== GENERATED_INITIAL_READING_ID) {
  throw new Error("Generated initial reading and public catalog order disagree.");
}

export function readingCommitment(reading: Reading): ReadingCommitment {
  const minutes = computeReadingMetrics(reading).uninterruptedMinutes;
  if (minutes >= 25) return "immersion";
  if (minutes >= 8) return "sitting";
  return "brief";
}

export function loadLessonLexicon(readingId: string) {
  return GENERATED_LESSON_LEXICON_LOADERS[readingId]?.() || Promise.resolve(null);
}

export function findDictionaryEntry(word: string, lexicon: LessonLexicon | null) {
  return lexicon?.entries[word.toLocaleLowerCase().replace(/[’']/g, "'").trim()];
}

export function readingTokenIds(reading: Reading) {
  const wordPattern = /^\p{L}+(?:[’'-]\p{L}+)*$/u;
  return reading.segments.flatMap((segment) =>
    segment.text
      .split(/(\p{L}+(?:[’'-]\p{L}+)*)/gu)
      .map((part, index) => (wordPattern.test(part) ? `${segment.id}-t${index}` : null))
      .filter((tokenId): tokenId is string => Boolean(tokenId)),
  );
}

export function recommendReadings(
  intentId: ReadingIntent | null,
  _currentReadingId?: string,
  commitment: ReadingCommitment = "brief",
  appetiteId: ReadingAppetite = "surprise",
  difficulty: ReadingLexicalDifficulty | null = null,
  form: ReadingForm | null = null,
  articulationId: ReadingArticulation | null = null,
) {
  const intent = READING_INTENTS.find((candidate) => candidate.id === intentId);
  const appetite = READING_APPETITES.find((candidate) => candidate.id === appetiteId);
  const articulation = READING_ARTICULATIONS.find((candidate) => candidate.id === articulationId);
  const targetMinutes = READING_COMMITMENTS.find((candidate) => candidate.id === commitment)?.targetMinutes || 5;
  return READINGS
    .map((reading) => {
      const outcomeMatches = intent ? reading.taxonomy.outcomes.filter((value) => intent.outcomes.includes(value)) : [];
      const moveMatches = intent ? reading.taxonomy.rhetoricalMoves.filter((value) => intent.rhetoricalMoves.includes(value)) : [];
      const matchedCraft = [...outcomeMatches, ...moveMatches].slice(0, 3);
      const moodMatches = appetite?.moods.filter((mood) => reading.taxonomy.mood.includes(mood)) || [];
      const familyMatch = appetite?.craftFamilies?.includes(reading.taxonomy.craftFamily) ? 1 : 0;
      const articulationOutcomeMatches = articulation
        ? reading.taxonomy.outcomes.filter((value) => articulation.outcomes.includes(value))
        : [];
      const articulationMoveMatches = articulation
        ? reading.taxonomy.rhetoricalMoves.filter((value) => articulation.rhetoricalMoves.includes(value))
        : [];
      const articulationMoodMatches = articulation?.moods?.filter((mood) => reading.taxonomy.mood.includes(mood)) || [];
      const articulationMatches = [
        ...articulationOutcomeMatches,
        ...articulationMoveMatches,
        ...articulationMoodMatches,
      ];
      const timeDistance = Math.abs(computeReadingMetrics(reading).uninterruptedMinutes - targetMinutes);
      const timeFit = Math.max(0, 12 - timeDistance);
      const commitmentMatch = readingCommitment(reading) === commitment;
      const difficultyMatch = !difficulty || reading.taxonomy.lexicalChallenge === difficulty;
      const formMatch = !form || reading.taxonomy.form === form;
      const appetiteMatch = appetiteId === "surprise" || moodMatches.length > 0 || familyMatch > 0;
      const intentMatch = !intent || matchedCraft.length > 0;
      const articulationMatch = !articulation || articulationMatches.length > 0;
      const supportedLandings = READING_ARTICULATIONS
        .filter((candidate) =>
          reading.taxonomy.outcomes.some((value) => candidate.outcomes.includes(value))
          || reading.taxonomy.rhetoricalMoves.some((value) => candidate.rhetoricalMoves.includes(value))
          || candidate.moods?.some((value) => reading.taxonomy.mood.includes(value))
        )
        .map((candidate) => candidate.label);
      const tradeoffs = [
        !commitmentMatch ? "a different reading length" : null,
        !appetiteMatch ? "a different emotional appetite" : null,
        !difficultyMatch ? "a different vocabulary level" : null,
        !intentMatch ? "a different language purpose" : null,
        !formMatch ? "a different form" : null,
        !articulationMatch ? "a different landing" : null,
      ].filter((value): value is string => Boolean(value));
      const matchedDimensions = [
        commitmentMatch ? "your time" : null,
        appetiteMatch ? "your appetite" : null,
        difficulty && difficultyMatch ? "your vocabulary level" : null,
        intent && intentMatch ? "your language purpose" : null,
        form && formMatch ? "your chosen form" : null,
        articulation && articulationMatch ? `${articulation.label.toLocaleLowerCase()} language` : null,
      ].filter((value): value is string => Boolean(value));
      const exactMatch = tradeoffs.length === 0;
      return {
        reading,
        commitmentMatch,
        difficultyMatch,
        formMatch,
        appetiteMatch,
        intentMatch,
        articulationMatch,
        exactMatch,
        tradeoffs,
        landingEvidence: supportedLandings.slice(0, 2),
        score: (exactMatch ? 100 : 0)
          + (commitmentMatch ? 24 : timeFit)
          + (appetiteMatch ? 22 : 0)
          + (difficulty && difficultyMatch ? 12 : 0)
          + (intent && intentMatch ? 16 : 0)
          + (form && formMatch ? 12 : 0)
          + (articulation && articulationMatch ? 16 : 0)
          + outcomeMatches.length * 3
          + moveMatches.length * 2
          + moodMatches.length * 4
          + familyMatch * 3
          + articulationOutcomeMatches.length * 3
          + articulationMoveMatches.length * 2
          + articulationMoodMatches.length * 3,
        reason: exactMatch
          ? `It fits ${matchedDimensions.join(", ")}. ${reading.whyItFits}`
          : `It is one of the closest maintained readings: it fits ${matchedDimensions.join(", ") || "the nearest available shape"}, with ${tradeoffs.join(" and ")}. ${reading.whyItFits}`,
      };
    })
    .sort((a, b) =>
      Number(b.exactMatch) - Number(a.exactMatch)
      || a.tradeoffs.length - b.tradeoffs.length
      || b.score - a.score
      || a.reading.title.localeCompare(b.reading.title)
    )
    .slice(0, 4);
}

export function findWordInsight(word: string) {
  for (const reading of READINGS) {
    const entry = reading.words[word];
    if (entry) return entry;
  }
  return undefined;
}
